import { Icon, Menu, Tooltip } from "@mindlercare/ui-web";
import { useGetPhraseLibrary } from "api/useIcbtQueries";
import type { Phrase } from "api/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { PhraseLibraryModal } from "./PhraseLibraryModal";

export type PhraseLibraryPickerProps = {
  /** Backend library id, e.g. "icbtassessments". */
  libraryId: string;
  /** Localized name of the field the phrase is inserted into (e.g. "Bedömning"). */
  targetFieldLabel: string;
  onInsert: (body: string) => void;
  /** Blocks both entry points while the target field can't be edited (e.g.
   * while fetching or saving the assessment). */
  disabled?: boolean;
};

/**
 * A single "variable button set" split pill (mirrors the AI-button layout in
 * GenerateDraft.tsx) with two entry points into the phrase library, joined by a
 * divider:
 * - left: a category-grouped "Select template" menu for quick insertion (pick →
 *   inserted), and
 * - right: an info icon that opens the full modal with filters + preview for
 *   discovery.
 *
 * Renders nothing until the library has published content.
 */
export const PhraseLibraryPicker = ({
  libraryId,
  targetFieldLabel,
  onInsert,
  disabled = false,
}: PhraseLibraryPickerProps) => {
  const { t } = useTranslation();
  const { data } = useGetPhraseLibrary(libraryId);
  const [isModalOpen, setModalOpen] = useState(false);

  const phrases = data?.phrases ?? [];
  const categories = data?.categories ?? [];

  if (phrases.length === 0) return null;

  const grouped = categories
    .map((category) => ({
      category,
      phrases: phrases.filter((p) => p.categoryKey === category.key),
    }))
    .filter((group) => group.phrases.length > 0);

  const uncategorised = phrases.filter(
    (p) => !p.categoryKey || !categories.some((c) => c.key === p.categoryKey),
  );

  const renderItem = (phrase: Phrase) => (
    <Menu.Item key={phrase.id} onClick={() => onInsert(phrase.body)}>
      {phrase.title}
    </Menu.Item>
  );

  return (
    <>
      <Pill>
        <MenuSlot>
          <Menu>
            <QuickTrigger
              variant="secondary"
              prefixIcon={<Icon.Add width={20} height={20} />}
              withExpandIcon
              disabled={disabled}
            >
              {t("PHRASE_LIBRARY_SELECT_TEMPLATE")}
            </QuickTrigger>
            <Items>
              {grouped.map(({ category, phrases: groupPhrases }) => (
                <Group key={category.key}>
                  <GroupLabel>{category.label}</GroupLabel>
                  {groupPhrases.map((phrase) => renderItem(phrase))}
                </Group>
              ))}
              {uncategorised.map((phrase) => renderItem(phrase))}
            </Items>
          </Menu>
        </MenuSlot>

        <PillDivider />

        <InfoTooltip
          content={t("PHRASE_LIBRARY_TRIGGER")}
          placement="bottom-end"
        >
          <InfoButton
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={disabled}
            data-testid="phrase-library-browse"
          >
            <Icon.Info width={20} height={20} />
          </InfoButton>
        </InfoTooltip>
      </Pill>

      <PhraseLibraryModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        libraryId={libraryId}
        targetFieldLabel={targetFieldLabel}
        onInsert={onInsert}
      />
    </>
  );
};

// A single pill-shaped split button (see GenerateDraft.tsx for the same
// "AI-button" layout): the "Select template" quick-pick menu on the left and an
// info icon (opens the full browse modal) on the right, separated by a divider.
const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  height: 33px;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  border-radius: 70px;
  overflow: hidden;
  flex-shrink: 0;
`;

const PillDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
`;

// Stretch the Menu's internal relative wrapper to the full pill height so the
// trigger (and its hover fill) covers the whole left zone.
const MenuSlot = styled.div`
  align-self: stretch;
  display: flex;

  & > div {
    display: flex;
  }
`;

// The flat "+ Select template ⌄" action. `&&` beats the Button variant's
// default height/padding/border so it sits flush inside the pill.
const QuickTrigger = styled(Menu.Trigger)`
  && {
    height: 100%;
    padding: 0 ${({ theme }) => theme.SPACING.SM} 0
      ${({ theme }) => theme.SPACING.MD};
    gap: ${({ theme }) => theme.SPACING.XS};
    border: none;
    border-radius: 0;
    background: transparent;
    color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
    font-size: ${({ theme }) => theme.FONT.SIZE.SMALL_MEDIUM};
  }

  &&:hover:not(:disabled) {
    border: none;
    background-color: ${({ theme }) => theme.COLORS.GRAY_LIGHT};
  }

  &&:disabled {
    border: none;
    background: transparent;
    opacity: 0.4;
  }
`;

// The Tooltip wraps its children in a plain block-level div; stretch it to the
// full pill height so the InfoButton (and its hover fill) covers the whole zone.
const InfoTooltip = styled(Tooltip)`
  align-self: stretch;
  display: flex;
`;

const InfoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 0 ${({ theme }) => theme.SPACING.SM};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.COLORS.GRAY_LIGHT};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const Items = styled(Menu.Items)`
  max-height: 280px;
  overflow-y: auto;
  border-radius: 4px;
`;

const Group = styled.div`
  & li {
    padding-left: ${({ theme }) => theme.SPACING.LG};
  }
`;

const GroupLabel = styled.span`
  display: inline-block;
  padding: ${({ theme }) => `${theme.SPACING.SM} ${theme.SPACING.MD}`};
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.BOLD};
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
`;
